// Generic "AI Summary" popup — summarizes whatever conversation/email text
// it's given (a Teams channel's messages, an open email) via the real
// POST /api/ai/chat endpoint, then offers a "Triage" action that hands off
// to the caller's own existing triage flow. Deliberately content-agnostic:
// TeamsPage.tsx and InboxPage.tsx each build their own prompt and onTriage
// callback; this component only owns the fetch/loading/render lifecycle.
import { useEffect, useRef, useState } from 'react';
import { pbAuthedFetch } from '../authHeaders';
import { renderAiText, aiErrorMessage } from '../lib/aiText';
import '../styles/ai-summary.css';

export function AiSummaryModal({
  title,
  subtitle,
  prompt,
  triageLabel = '⚡ Triage',
  onTriage,
  onClose,
  onSummaryReady,
}: {
  title: string;
  subtitle?: string;
  prompt: string;
  triageLabel?: string;
  onTriage: () => void;
  onClose: () => void;
  /** Called once with the generated text as soon as it arrives, so the
   * caller can stash it (e.g. to seed a ticket's work notes on Triage)
   * without this component needing to know what Triage does. */
  onSummaryReady?: (summary: string) => void;
}) {
  const [state, setState] = useState<{ loading: boolean; error: string | null; summary: string | null }>({
    loading: true,
    error: null,
    summary: null,
  });
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return; // StrictMode double-invoke guard — this fires a real, rate-limited network call
    ranRef.current = true;
    (async () => {
      try {
        const res = await pbAuthedFetch('/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.reply) {
          setState({ loading: false, error: aiErrorMessage(res.status, data), summary: null });
          return;
        }
        const summary = String(data.reply).trim();
        setState({ loading: false, error: null, summary });
        onSummaryReady?.(summary);
      } catch {
        setState({ loading: false, error: 'AI request failed — check your connection and try again.', summary: null });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="aism-overlay" onClick={onClose}>
      <div className="aism-card" onClick={(e) => e.stopPropagation()}>
        <div className="aism-head">
          <div>
            <div className="aism-title">✨ AI Summary — {title}</div>
            {subtitle && <div className="aism-subtitle">{subtitle}</div>}
          </div>
          <button className="aism-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="aism-body">
          {state.loading && (
            <div className="aism-loading">
              <span className="aism-spinner" />
              Generating summary…
            </div>
          )}
          {state.error && <div className="aism-error">{state.error}</div>}
          {state.summary && <div className="aism-summary">{renderAiText(state.summary)}</div>}
        </div>
        <div className="aism-foot">
          <button className="aism-btn" onClick={onClose}>Close</button>
          <button className="aism-btn aism-btn--primary" onClick={onTriage}>{triageLabel}</button>
        </div>
      </div>
    </div>
  );
}
